import { type SelectValue } from '@procurify/ui'
import moment from 'moment'

import {
  CheckboxCustomFieldValues,
  type CustomField,
  CustomFieldTypes,
  CustomFieldValue,
} from '@webapp/core/entities/internal/CustomField'
import {
  type DateCustomFieldFormSchema,
  type CustomFieldFormSchema,
  type CheckboxCustomFieldFormSchema,
  type DropdownCustomFieldFormSchema,
  type TextCustomFieldFormSchema,
} from '@webapp/core/interfaces/forms/CustomFieldFormSchema'
import {
  type CheckboxCustomFieldValueFormSchema,
  type DateCustomFieldValueFormSchema,
  type DropdownCustomFieldValueFormSchema,
  type CustomFieldValueFormSchema,
  type TextCustomFieldValueFormSchema,
} from '@webapp/core/interfaces/forms/CustomFieldValueFormSchema'
import { formatYYYYMMDD } from '../dates'

export function isTextCustomFieldFormValue(
  value: CustomFieldValueFormSchema
): value is TextCustomFieldValueFormSchema {
  return value.field.type === CustomFieldTypes.TEXT
}

export function isDateCustomFieldFormValue(
  value: CustomFieldValueFormSchema
): value is DateCustomFieldValueFormSchema {
  return value.field.type === CustomFieldTypes.DATE
}

export function isDropdownCustomFieldFormValue(
  value: CustomFieldValueFormSchema
): value is DropdownCustomFieldValueFormSchema {
  return value.field.type === CustomFieldTypes.DROPDOWN
}

export function isCheckboxCustomFieldFormValue(
  value: CustomFieldValueFormSchema
): value is CheckboxCustomFieldValueFormSchema {
  return value.field.type === CustomFieldTypes.CHECKBOX
}

export function isTextCustomFieldValue(
  value: CustomFieldValue
): value is CustomFieldValue<string> {
  return value.field.type === CustomFieldTypes.TEXT
}

export function isDateCustomFieldValue(
  value: CustomFieldValue
): value is CustomFieldValue<ISODateString> {
  return value.field.type === CustomFieldTypes.DATE
}

export function isDropdownCustomFieldValue(
  value: CustomFieldValue
): value is CustomFieldValue<string> {
  return value.field.type === CustomFieldTypes.DROPDOWN
}

export function isCheckboxCustomFieldValue(
  value: CustomFieldValue
): value is CustomFieldValue<boolean> {
  return value.field.type === CustomFieldTypes.CHECKBOX
}

/** The custom field types whose values the values endpoint can list. */
export const CUSTOM_FIELD_WITH_VALUE_OPTIONS_TYPES = [
  CustomFieldTypes.DROPDOWN,
  CustomFieldTypes.CHECKBOX,
] as const

export type CustomFieldWithValueOptions = CustomField & {
  type: (typeof CUSTOM_FIELD_WITH_VALUE_OPTIONS_TYPES)[number]
}

export function isCustomFieldWithValueOptions(
  customField: CustomField
): customField is CustomFieldWithValueOptions {
  return CUSTOM_FIELD_WITH_VALUE_OPTIONS_TYPES.some(
    (customFieldType) => customFieldType === customField.type
  )
}

/** Case-insensitive, since stored checkbox values are not consistently cased. */
export function isCheckedCheckboxCustomFieldValue(value: string): boolean {
  return value.toLowerCase() === CheckboxCustomFieldValues.CHECKED.toLowerCase()
}

const getDropdownValue = (
  customFieldValue: CustomFieldValue<string>,
  customFieldFieldChoices: CustomField['choices']
): SelectValue | null => {
  const { value } = customFieldValue
  return value && customFieldFieldChoices.includes(value)
    ? { label: value, value }
    : null
}

const mapCustomFieldToCustomFieldFormSchema = (
  cf: CustomField
): CustomFieldFormSchema => {
  return {
    id: cf.id,
    choices: cf.choices.map((choice) => ({
      label: choice,
      value: choice,
    })),
    name: cf.name,
    type: cf.type,
    required: cf.required,
    active: cf.active,
  }
}

export const mapCustomFieldValueToCustomFieldValueFormSchema = (
  customFieldValue: CustomFieldValue
): CustomFieldValueFormSchema => {
  if (isDateCustomFieldValue(customFieldValue)) {
    const schema: DateCustomFieldValueFormSchema = {
      id: customFieldValue ? customFieldValue.id : null,
      field: mapCustomFieldToCustomFieldFormSchema(
        customFieldValue.field
      ) as DateCustomFieldFormSchema,
      value:
        customFieldValue.value && isDateCustomFieldValue(customFieldValue)
          ? moment(customFieldValue.value)
          : undefined,
    }

    return schema
  } else if (isCheckboxCustomFieldValue(customFieldValue)) {
    const schema: CheckboxCustomFieldValueFormSchema = {
      id: customFieldValue ? customFieldValue.id : null,
      field: mapCustomFieldToCustomFieldFormSchema(
        customFieldValue.field
      ) as CheckboxCustomFieldFormSchema,
      value:
        customFieldValue && isCheckboxCustomFieldValue(customFieldValue)
          ? Boolean(customFieldValue.value)
          : false,
    }

    return schema
  } else if (isDropdownCustomFieldValue(customFieldValue)) {
    const schema: DropdownCustomFieldValueFormSchema = {
      id: customFieldValue ? customFieldValue.id : null,
      field: mapCustomFieldToCustomFieldFormSchema(
        customFieldValue.field
      ) as DropdownCustomFieldFormSchema,
      value:
        customFieldValue && isDropdownCustomFieldValue(customFieldValue)
          ? getDropdownValue(customFieldValue, customFieldValue.field.choices)
          : null,
    }

    return schema
  } else {
    const schema: TextCustomFieldValueFormSchema = {
      id: customFieldValue ? customFieldValue.id : null,
      field: mapCustomFieldToCustomFieldFormSchema(
        customFieldValue.field
      ) as TextCustomFieldFormSchema,
      value:
        customFieldValue.value && isTextCustomFieldValue(customFieldValue)
          ? customFieldValue.value
          : '',
    }

    return schema
  }
}

export const mapCustomFieldFormValueToCustomFieldValue = (
  customFieldFormSchemas: CustomFieldValueFormSchema[]
): CustomFieldValue[] => {
  return customFieldFormSchemas
    .map((cfSchema) => {
      let value: string | boolean | null = null

      if (isTextCustomFieldFormValue(cfSchema)) {
        value = cfSchema.value
      } else if (isDateCustomFieldFormValue(cfSchema)) {
        value = cfSchema.value ? formatYYYYMMDD(cfSchema.value.toDate()) : null
      } else if (isCheckboxCustomFieldFormValue(cfSchema)) {
        value = cfSchema.value
      } else if (isDropdownCustomFieldFormValue(cfSchema)) {
        value = cfSchema.value?.value || null
      }

      return new CustomFieldValue({
        field: {
          id: cfSchema.field.id,
          choices: cfSchema.field.choices.map((choice) => choice.value),
          name: cfSchema.field.name,
          required: cfSchema.field.required,
          type: cfSchema.field.type,
          active: cfSchema.field.active,
          // Form schemas don't carry the definition default — irrelevant for value submission.
          defaultValue: '',
        },
        id: cfSchema.id,
        value,
      })
    })
    .filter((cfSchema) => cfSchema !== null)
}
